/**
 * MonthView — Month-level calendar grid time entry form.
 * Extracted from TimeTracking.jsx for maintainability.
 */
import { Card, CardHeader, CardDescription, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, Save, FileDown } from 'lucide-react';

const DAYS_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

export const MonthView = ({
  monthDates,
  monthData,
  setMonthData,
  monthRef,
  setMonthRef,
  monthTotal,
  saving,
  handleSaveMonth,
  applyContractScheduleMonth,
  selectedContract,
  getContractName,
  getSelectedContract,
  getHolidayName,
  getTodayStr,
  parseLocalDate,
}) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="icon" className="h-8 w-8 shrink-0"
                onClick={() => {
                  const [y, m] = monthRef.split('-').map(Number);
                  const prev = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
                  setMonthRef(prev);
                }}
                data-testid="month-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold text-center capitalize whitespace-nowrap" data-testid="month-label">
                {new Date(monthRef + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </span>
              <Button
                variant="outline" size="icon" className="h-8 w-8 shrink-0"
                onClick={() => {
                  const [y, m] = monthRef.split('-').map(Number);
                  const next = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
                  setMonthRef(next);
                }}
                data-testid="month-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Badge variant="outline" className="text-sm font-mono px-2 py-1 shrink-0" data-testid="month-total">
              {monthTotal.toFixed(1)}h
            </Badge>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSaveMonth} disabled={saving}
              style={{ backgroundColor: 'var(--primary-400)' }} size="sm"
              className="w-full sm:w-auto" data-testid="save-month-btn"
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Enregistrement...' : 'Enregistrer le mois'}
            </Button>
          </div>
        </div>
        <CardDescription>
          Saisissez le nombre d'heures par jour ({getContractName(selectedContract)})
        </CardDescription>
        <div className="flex flex-wrap gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={applyContractScheduleMonth} className="text-xs" data-testid="apply-schedule-month-btn">
            <FileDown className="h-3.5 w-3.5 mr-1" /> Appliquer horaires du contrat
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Header */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAYS_SHORT.map(d => (
            <div key={d} className="text-center text-xs font-semibold text-[var(--text-secondary)] py-1">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <TooltipProvider delayDuration={200}>
          <div className="grid grid-cols-7 gap-1">
            {(() => {
              const firstDay = parseLocalDate(monthDates[0]).getDay();
              const offset = firstDay === 0 ? 6 : firstDay - 1;
              return Array(offset).fill(null).map((_, i) => <div key={`blank-${i}`} className="h-[60px]" />);
            })()}

            {monthDates.map((dateStr) => {
              const data = monthData[dateStr] || { hours: '' };
              const dayNum = parseInt(dateStr.split('-')[2], 10);
              const isToday = dateStr === getTodayStr();
              const isWeekend = [0, 6].includes(parseLocalDate(dateStr).getDay());
              const holidayName = getHolidayName(dateStr);
              const contract = getSelectedContract();
              const canWorkHoliday = contract?.work_on_holidays === true;
              const isBlockedHoliday = holidayName && !canWorkHoliday;
              const isLeavePending = data.is_leave_pending;

              // Build tooltip text
              let tooltipText = null;
              if (data.is_absent && data.absence_type) {
                tooltipText = data.absence_type;
              } else if (isLeavePending && data.absence_type) {
                tooltipText = data.absence_type;
              }
              if (holidayName) {
                tooltipText = tooltipText ? `${holidayName} · ${tooltipText}` : holidayName;
              }

              const cell = (
                <div
                  key={dateStr}
                  className={cn(
                    "relative rounded-lg border p-1.5 flex flex-col items-center justify-center h-[60px] transition-all duration-150",
                    holidayName && !data.is_absent && !isLeavePending && "bg-[#E17A73]/8 border-[#E17A73]/40",
                    data.is_absent && "bg-[var(--warning)]/10 border-[var(--warning)]",
                    isLeavePending && !data.is_absent && "bg-[var(--warning)]/5 border-[var(--warning)]/40 border-dashed",
                    isToday && !data.is_absent && !holidayName && !isLeavePending && "bg-[var(--primary-50)] border-[var(--primary-400)] ring-1 ring-[var(--primary-400)]",
                    isWeekend && !isToday && !data.is_absent && !holidayName && !isLeavePending && "bg-[var(--neutral-50)] border-[var(--neutral-200)]",
                    !isToday && !isWeekend && !data.is_absent && !holidayName && !isLeavePending && "border-[var(--neutral-200)]"
                  )}
                  data-testid={`month-cell-${dayNum}`}
                >
                  <span className={cn(
                    "text-xs font-semibold leading-none",
                    holidayName && "text-[#E17A73]",
                    isToday && !holidayName ? "text-[var(--primary-400)]" : !holidayName && "text-[var(--text-secondary)]"
                  )}>
                    {dayNum}
                  </span>

                  {holidayName && !data.is_absent && !isLeavePending && isBlockedHoliday && (
                    <span className="text-[7px] text-[#E17A73] font-medium leading-tight mt-0.5 text-center truncate w-full px-0.5" data-testid={`month-holiday-${dayNum}`}>
                      {holidayName.length > 8 ? holidayName.substring(0, 7) + '…' : holidayName}
                    </span>
                  )}

                  {holidayName && canWorkHoliday && !data.is_absent && !isLeavePending && (
                    <span className="text-[6px] text-[#E17A73] font-medium leading-tight mt-0.5 text-center truncate w-full px-0.5" data-testid={`month-holiday-${dayNum}`}>
                      Férié
                    </span>
                  )}

                  {data.is_absent ? (
                    <span className="text-[8px] text-[var(--warning)] font-medium mt-0.5 text-center leading-tight truncate w-full px-0.5">
                      {data.absence_type
                        ? (data.absence_type.length > 10 ? data.absence_type.substring(0, 9) + '…' : data.absence_type)
                        : 'Abs.'}
                    </span>
                  ) : isLeavePending ? (
                    <span className="text-[8px] text-[var(--warning)]/70 font-medium mt-0.5 text-center leading-tight italic">
                      Att.
                    </span>
                  ) : isBlockedHoliday ? null : (
                    <Input
                      type="number" step="0.5" min="0" max="24" value={data.hours}
                      onChange={(e) => {
                        const newData = { ...monthData };
                        newData[dateStr] = { ...data, hours: e.target.value };
                        setMonthData(newData);
                      }}
                      className="h-6 w-full text-xs text-center font-mono p-0 border-0 bg-transparent focus:bg-[var(--bg-primary)] focus:border mt-0.5"
                      placeholder="—" data-testid={`month-hours-${dayNum}`}
                    />
                  )}

                  {data.has_entry && !data.is_absent && (
                    <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-[var(--success)]" />
                  )}
                </div>
              );

              // Wrap with tooltip if there's a label to show
              if (tooltipText) {
                return (
                  <Tooltip key={dateStr}>
                    <TooltipTrigger asChild>{cell}</TooltipTrigger>
                    <TooltipContent side="top" className="text-xs py-1 px-2 max-w-[200px]">
                      {tooltipText}
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return cell;
            })}
          </div>
        </TooltipProvider>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 text-xs text-[var(--text-secondary)] flex-wrap">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-[var(--success)]" /><span>Déjà saisi</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-[var(--warning)]" /><span>Absence / Congé</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded border-2 border-[var(--primary-400)]" /><span>Aujourd'hui</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-[#E17A73]/10 border border-[#E17A73]/40" /><span>Jour férié</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded border border-dashed border-[var(--warning)]/40" /><span>Congé en attente</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
